import { SIZE_CHART } from '../components/sizes/data-sample'

// Type cho dữ liệu bảng size
type SizeTableRow = Record<string, string>

// BMI calculation
const calculateBMI = (weight: number, height: number): number => {
  const heightInMeters = height / 100
  return weight / (heightInMeters * heightInMeters)
}

// Dynamic size recommendation logic
const getRecommendedSize = (
  weight: number,
  height: number,
  shape: string,
  sizeChart: typeof SIZE_CHART
): string[] => {
  const bmi = calculateBMI(weight, height)

  // Find base size from weight range
  let baseSizeIndex = sizeChart.findIndex((size) => weight >= size.minWeight && weight <= size.maxWeight)

  // If weight is outside all ranges, find closest
  if (baseSizeIndex === -1) {
    if (weight < sizeChart[0].minWeight) {
      baseSizeIndex = 0
    } else if (weight > sizeChart[sizeChart.length - 1].maxWeight) {
      baseSizeIndex = sizeChart.length - 1
    } else {
      // Find closest range
      baseSizeIndex = sizeChart.reduce((closest, size, idx) => {
        const currentDiff = Math.abs(weight - (size.minWeight + size.maxWeight) / 2)
        const closestDiff = Math.abs(
          weight - (sizeChart[closest].minWeight + sizeChart[closest].maxWeight) / 2
        )
        return currentDiff < closestDiff ? idx : closest
      }, 0)
    }
  }

  const recommendedIndices: number[] = []

  // BMI-based adjustment factors
  const isUnderweight = bmi < 18.5
  const isNormal = bmi >= 18.5 && bmi < 25
  const isOverweight = bmi >= 25 && bmi < 30
  const isObese = bmi >= 30

  // Shape-based logic
  if (shape === 'slim') {
    if (isUnderweight) {
      // Very slim: recommend smaller or same
      recommendedIndices.push(Math.max(0, baseSizeIndex - 1), baseSizeIndex)
    } else {
      // Normal slim: recommend base + one up
      recommendedIndices.push(baseSizeIndex, Math.min(sizeChart.length - 1, baseSizeIndex + 1))
    }
  } else if (shape === 'balanced') {
    if (isUnderweight || isNormal) {
      // Balanced normal: give range around base
      recommendedIndices.push(
        Math.max(0, baseSizeIndex - 1),
        baseSizeIndex,
        Math.min(sizeChart.length - 1, baseSizeIndex + 1)
      )
    } else {
      // Higher BMI balanced: prefer larger
      recommendedIndices.push(
        baseSizeIndex,
        Math.min(sizeChart.length - 1, baseSizeIndex + 1),
        Math.min(sizeChart.length - 1, baseSizeIndex + 2)
      )
    }
  } else if (shape === 'full') {
    if (isNormal) {
      // Full but normal BMI: base + larger
      recommendedIndices.push(baseSizeIndex, Math.min(sizeChart.length - 1, baseSizeIndex + 1))
    } else {
      // Higher BMI full: recommend larger sizes
      recommendedIndices.push(
        baseSizeIndex,
        Math.min(sizeChart.length - 1, baseSizeIndex + 1),
        Math.min(sizeChart.length - 1, baseSizeIndex + 2)
      )
    }
  } else {
    // Default/unknown shape: balanced approach
    recommendedIndices.push(
      Math.max(0, baseSizeIndex - 1),
      baseSizeIndex,
      Math.min(sizeChart.length - 1, baseSizeIndex + 1)
    )
  }

  // Remove duplicates, get unique valid sizes, limit to 2-3 recommendations
  const uniqueIndices = [...new Set(recommendedIndices)]
    .filter((idx) => idx >= 0 && idx < sizeChart.length)
    .sort((a, b) => a - b)

  // Return top 1 most relevant sizes
  const recommendedSizes = uniqueIndices.slice(0, 1).map((idx) => sizeChart[idx].label)

  return recommendedSizes
}

const parseTableToObjects = (html: string): SizeTableRow[] => {
  try {
    // Kiểm tra input
    if (!html || typeof html !== 'string' || html.trim() === '') {
      return []
    }

    const doc = new DOMParser().parseFromString(html, 'text/html')
    const rows = [...doc.querySelectorAll('tr')]

    // Kiểm tra có ít nhất 2 rows (header + data)
    if (rows.length < 2) {
      return []
    }

    // Lấy headers từ row đầu tiên
    const headerRow = rows[0]
    if (!headerRow || !headerRow.children || headerRow.children.length === 0) {
      return []
    }

    const headers = [...headerRow.children]
      .map((td) => td.textContent?.trim() || '')
      .filter((header) => header !== '') // Loại bỏ headers rỗng

    // Kiểm tra có headers hợp lệ
    if (headers.length === 0) {
      return []
    }

    // Parse các rows còn lại
    const dataRows = rows.slice(1).map((row) => {
      if (!row || !row.children) return null

      const cells = [...row.children].map((td) => td.textContent?.trim() || '')

      // Chỉ tạo object nếu có ít nhất một cell không rỗng
      const hasData = cells.some((cell) => cell !== '')
      if (!hasData) return null

      const rowObject: SizeTableRow = headers.reduce((obj, key, index) => {
        obj[key] = cells[index] || ''
        return obj
      }, {} as SizeTableRow)

      return rowObject
    })

    // Filter null values
    return dataRows.filter((row): row is SizeTableRow => row !== null)
  } catch (error) {
    console.error('Error parsing size table:', error)
    return []
  }
}

export { calculateBMI, getRecommendedSize, parseTableToObjects }
